import React, { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Ruler, Clipboard, X } from "lucide-react";

/**
 * Paint-depth (PTG) readings for a job. Each reading is
 *   { id, panel, value_um, note?, taken_at }
 * and the panel is a free-text name (front-left fender, bonnet, etc.).
 * Readings >= FILLER_THRESHOLD µm surface a "filler suspected" warning.
 */
export const FILLER_THRESHOLD = 300;
const COMMON_PANELS = [
  "Bonnet", "Roof", "Boot", "Front bumper", "Rear bumper",
  "Front L fender", "Front R fender", "Rear L quarter", "Rear R quarter",
  "Door FL", "Door FR", "Door RL", "Door RR",
];

export default function PaintDepthPanel({ job, canEdit = true, onChange }) {
  const readings = (job.ptg_readings || []).slice().sort(
    (a, b) => (a.taken_at || "").localeCompare(b.taken_at || ""),
  );
  const [adding, setAdding] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [form, setForm] = useState({ panel: "", value_um: "", note: "" });
  const [bulk, setBulk] = useState("");
  const [busy, setBusy] = useState(false);

  const persist = async (newList) => {
    setBusy(true);
    try {
      await api.put(`/jobs/${job.id}`, { ...job, ptg_readings: newList });
      onChange?.();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save reading");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addReading = async () => {
    const val = Number(form.value_um);
    if (!form.panel.trim()) return toast.error("Enter a panel name");
    if (!Number.isFinite(val) || val <= 0) return toast.error("Enter a positive µm value");
    const next = [
      ...readings,
      {
        id: `ptg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        panel: form.panel.trim(),
        value_um: val,
        note: form.note.trim(),
        taken_at: new Date().toISOString(),
      },
    ];
    const ok = await persist(next);
    if (!ok) return;
    setForm({ panel: "", value_um: "", note: "" });
    setAdding(false);
    if (val >= FILLER_THRESHOLD) {
      toast.warning(`${val} µm on ${form.panel} — filler suspected`);
    } else {
      toast.success("Reading saved");
    }
  };

  const remove = async (id) => {
    const ok = await persist(readings.filter((r) => r.id !== id));
    if (ok) toast.success("Reading removed");
  };

  // Bulk paste parser — accepts NexPTG-style rows like:
  //   Bonnet: 145
  //   Front bumper - 210
  //   Door FL,180
  //   Roof|132 µm
  const importBulk = async () => {
    const rows = bulk.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const parsed = [];
    for (const line of rows) {
      const m = line.match(/^(.+?)\s*[:\-|,]\s*([0-9]+(?:\.[0-9]+)?)/);
      if (!m) continue;
      const panel = m[1].trim();
      const val = Number(m[2]);
      if (!panel || !Number.isFinite(val)) continue;
      parsed.push({
        id: `ptg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        panel,
        value_um: val,
        note: "",
        taken_at: new Date().toISOString(),
      });
    }
    if (parsed.length === 0) return toast.error("Could not parse any rows — expected 'panel: value' or 'panel, value'");
    const next = [...readings, ...parsed];
    const ok = await persist(next);
    if (!ok) return;
    setBulk("");
    setShowBulk(false);
    toast.success(`Imported ${parsed.length} reading${parsed.length === 1 ? "" : "s"}`);
  };

  const maxReading = readings.reduce((m, r) => Math.max(m, r.value_um), 0);
  const flagged = readings.filter((r) => r.value_um >= FILLER_THRESHOLD);

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3" data-testid={`ptg-panel-${job.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
        <div className="text-label flex items-center gap-1.5">
          <Ruler className="w-3 h-3" /> Paint depth · NexPTG
          {readings.length > 0 && (
            <span className="text-slate-500 text-[10px] ml-1">
              {readings.length} reading{readings.length === 1 ? "" : "s"} · max {maxReading} µm
            </span>
          )}
        </div>
        {canEdit && (
          <div className="flex gap-1.5 flex-wrap">
            {!adding && !showBulk && (
              <>
                <button
                  data-testid={`ptg-add-${job.id}`}
                  onClick={() => setAdding(true)}
                  className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 rounded-full px-2.5 py-1"
                >
                  <Plus className="w-3 h-3" /> Add reading
                </button>
                <button
                  data-testid={`ptg-paste-${job.id}`}
                  onClick={() => setShowBulk(true)}
                  className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-200 rounded-full px-2.5 py-1"
                  title="Paste rows from a NexPTG email report"
                >
                  <Clipboard className="w-3 h-3" /> Paste report
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filler warning summary */}
      {flagged.length > 0 && (
        <div
          data-testid={`ptg-warning-${job.id}`}
          className="mb-2 flex items-start gap-2 bg-amber-500/10 border border-amber-400/30 rounded-xl px-3 py-2 text-xs text-amber-100"
        >
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-300" />
          <div>
            <b>Filler suspected</b> on {flagged.length} panel{flagged.length === 1 ? "" : "s"} —
            reading{flagged.length === 1 ? "" : "s"} ≥ {FILLER_THRESHOLD} µm. Photograph before wrapping.
          </div>
        </div>
      )}

      {/* Reading list */}
      {readings.length === 0 && !adding && !showBulk && (
        <div className="text-xs text-slate-500 italic py-1">No readings yet.</div>
      )}
      <div className="space-y-1.5">
        {readings.map((r) => {
          const flag = r.value_um >= FILLER_THRESHOLD;
          return (
            <div
              key={r.id}
              data-testid={`ptg-reading-${r.id}`}
              className={`flex items-center gap-2 rounded-xl px-3 py-1.5 text-xs ${flag ? "bg-amber-500/10 border border-amber-400/30" : "bg-black/30 border border-white/5"}`}
            >
              <span className="text-slate-300 min-w-[8rem] truncate">{r.panel}</span>
              <span className={`font-mono ${flag ? "text-amber-200" : "text-white"}`}>{r.value_um} µm</span>
              {flag && <AlertTriangle className="w-3 h-3 text-amber-300" />}
              {r.note && <span className="text-slate-500 truncate flex-1">· {r.note}</span>}
              {canEdit && (
                <button
                  data-testid={`ptg-delete-${r.id}`}
                  onClick={() => remove(r.id)}
                  className="ml-auto p-1 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Add-reading form */}
      {adding && (
        <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid={`ptg-form-${job.id}`}>
          <div className="grid grid-cols-2 gap-2">
            <label className="col-span-2">
              <span className="text-label block mb-1">Panel</span>
              <input
                data-testid={`ptg-panel-input-${job.id}`}
                list={`ptg-panels-${job.id}`}
                value={form.panel}
                onChange={(e) => setForm({ ...form, panel: e.target.value })}
                placeholder="e.g. Bonnet"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder-slate-500"
              />
              <datalist id={`ptg-panels-${job.id}`}>
                {COMMON_PANELS.map((p) => <option key={p} value={p} />)}
              </datalist>
            </label>
            <label>
              <span className="text-label block mb-1">µm</span>
              <input
                data-testid={`ptg-value-${job.id}`}
                type="number"
                min="0"
                step="1"
                value={form.value_um}
                onChange={(e) => setForm({ ...form, value_um: e.target.value })}
                placeholder="e.g. 145"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder-slate-500"
              />
            </label>
            <label>
              <span className="text-label block mb-1">Note (optional)</span>
              <input
                data-testid={`ptg-note-${job.id}`}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="Chip, scratch, colour patch…"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs text-white placeholder-slate-500"
              />
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setAdding(false); setForm({ panel: "", value_um: "", note: "" }); }}
              className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-full px-3 py-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              data-testid={`ptg-save-${job.id}`}
              onClick={addReading}
              disabled={busy}
              className="text-[10px] bg-white text-black font-semibold rounded-full px-3 py-1 disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save reading"}
            </button>
          </div>
        </div>
      )}

      {/* Bulk paste import */}
      {showBulk && (
        <div className="mt-3 border-t border-white/10 pt-3 space-y-2" data-testid={`ptg-bulk-${job.id}`}>
          <div className="text-label">Paste NexPTG rows</div>
          <textarea
            data-testid={`ptg-bulk-textarea-${job.id}`}
            value={bulk}
            onChange={(e) => setBulk(e.target.value)}
            rows={5}
            placeholder={"Bonnet: 145\nRoof: 132\nFront L fender - 210\nDoor FL, 180"}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-xs text-white placeholder-slate-500 font-mono"
          />
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setShowBulk(false); setBulk(""); }}
              className="text-[10px] flex items-center gap-1 bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 rounded-full px-3 py-1"
            >
              <X className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              data-testid={`ptg-bulk-import-${job.id}`}
              onClick={importBulk}
              disabled={busy}
              className="text-[10px] bg-white text-black font-semibold rounded-full px-3 py-1 disabled:opacity-60"
            >
              Import
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact summary for lists (e.g. ContactJobHistory, DetailsModal). */
export function ptgSummary(job) {
  const r = job.ptg_readings || [];
  if (r.length === 0) return null;
  const max = r.reduce((m, x) => Math.max(m, x.value_um || 0), 0);
  const flagged = r.filter((x) => (x.value_um || 0) >= FILLER_THRESHOLD).length;
  return { count: r.length, max, flagged };
}
